import { createUserSchema, ROLE_LABEL, ROLES, type Paginated, type Role, type UserDto } from '@bnn/shared';
import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Alert, Badge, Button, Card, Field, Input, Modal, PageHeader, Pagination, SecretBox, Select, Table, useDebounced } from '../components/ui';
import { api, qs } from '../lib/api';
import { errMsg, useApi } from '../lib/useApi';

export default function UsersPage() {
  const { user: me } = useAuth();
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim());
  const [page, setPage] = useState(1);
  const list = useApi<Paginated<UserDto>>(`/users${qs({ search: q, page, page_size: 50 })}`);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null);

  const patch = async (u: UserDto, body: Partial<{ role: Role; is_active: boolean; reset_password: true }>) => {
    setError('');
    try {
      const r = await api.patch<{ user: UserDto; temporary_password?: string }>(`/users/${u.id}`, body);
      if (r.temporary_password) setSecret({ email: u.email, password: r.temporary_password });
      list.reload();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <>
      <PageHeader
        title="ผู้ใช้"
        subtitle="Admin สร้างบัญชีและกำหนดสิทธิ์ · ผู้ใช้ใหม่ได้รหัสผ่านชั่วคราวและต้องเปลี่ยนเมื่อเข้าครั้งแรก"
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            + เพิ่มผู้ใช้
          </Button>
        }
      />
      {secret && (
        <div className="mb-4">
          <SecretBox label={`รหัสผ่านชั่วคราวของ ${secret.email}`} value={secret.password} />
          <Button size="sm" variant="ghost" className="mt-1" onClick={() => setSecret(null)}>
            ซ่อน
          </Button>
        </div>
      )}
      {error && (
        <div className="mb-3">
          <Alert>{error}</Alert>
        </div>
      )}
      <Card>
        <Input
          type="search"
          placeholder="ค้นหาชื่อหรืออีเมล"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="mb-3"
        />
        {list.error && <Alert>{list.error.message}</Alert>}
        {list.data && (
          <>
            <Table head={['ชื่อ', 'Role', 'สถานะ', 'เข้าใช้ล่าสุด', '']} minWidth={720}>
              {list.data.data.map((u) => (
                <tr key={u.id} className={u.is_active ? '' : 'text-muted'}>
                  <td>
                    <div className="font-medium">
                      {u.display_name} {u.id === me?.id && <Badge>คุณ</Badge>}
                    </div>
                    <div className="text-xs text-muted">{u.email}</div>
                  </td>
                  <td>
                    <Select
                      value={u.role}
                      disabled={u.id === me?.id}
                      onChange={(e) => void patch(u, { role: e.target.value as Role })}
                      className="py-1.5 text-sm"
                      aria-label="Role"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="space-x-1 whitespace-nowrap">
                    {u.is_active ? <Badge tone="ok">ใช้งาน</Badge> : <Badge>ปิดใช้งาน</Badge>}
                    {u.must_change_password && <Badge tone="warn">รอเปลี่ยนรหัส</Badge>}
                  </td>
                  <td className="text-xs whitespace-nowrap text-muted">{u.last_login_at ? new Date(u.last_login_at).toLocaleString('th-TH') : '-'}</td>
                  <td className="space-x-1 text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => confirm(`รีเซ็ตรหัสผ่านของ ${u.email}?`) && void patch(u, { reset_password: true })}>
                      รีเซ็ตรหัส
                    </Button>
                    {u.id !== me?.id && (
                      <Button
                        size="sm"
                        variant={u.is_active ? 'danger' : 'secondary'}
                        onClick={() => (!u.is_active || confirm(`ปิดใช้งาน ${u.email}? (ประวัติการนับยังอยู่)`)) && void patch(u, { is_active: !u.is_active })}
                      >
                        {u.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <Pagination page={list.data.page} pageSize={list.data.page_size} total={list.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      <Modal open={adding} title="เพิ่มผู้ใช้" onClose={() => setAdding(false)}>
        <AddUserForm
          onDone={(email, password) => {
            setAdding(false);
            setSecret({ email, password });
            list.reload();
          }}
        />
      </Modal>
    </>
  );
}

function AddUserForm({ onDone }: { onDone: (email: string, password: string) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('COUNTER');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const parsed = createUserSchema.safeParse({ email, display_name: name, role });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง');
    setBusy(true);
    try {
      const r = await api.post<{ user: UserDto; temporary_password: string }>('/users', parsed.data);
      onDone(r.user.email, r.temporary_password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <Alert>{error}</Alert>}
      <Field label="อีเมล *">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </Field>
      <Field label="ชื่อที่แสดง *">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Role">
        <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </Select>
      </Field>
      <Button type="submit" variant="primary" className="w-full" loading={busy}>
        สร้างผู้ใช้
      </Button>
    </form>
  );
}
